import type { Hall } from '../api/types';

/**
 * The city a venue is in, for grouping and the location lists.
 *
 * Venues listed before the City field was filled in carry their town in the
 * locality, or alone in the address line ("palakkad"), so reading only `city`
 * left every such venue out of the city lists — the site showed one city while
 * venues stood in several. The fallbacks only take a short place name, never a
 * street address, and the result is title-cased so "palakkad" and "Palakkad"
 * are one city.
 */
export function cityOf(h: Pick<Hall, 'city' | 'locality' | 'location'>): string {
  const pick = [h.city, h.locality, h.location]
    .map((s) => (s ?? '').trim())
    .find((s, i) => s && (i === 0 || looksLikePlace(s)));
  return pick ? titleCase(pick) : '';
}

/** One to three words of letters — a town, not "12/4 M.G. Road, Near Bus Stand". */
const looksLikePlace = (s: string) => /^[\p{L}][\p{L}\s.'-]{0,40}$/u.test(s) && s.split(/\s+/).length <= 3;

const titleCase = (s: string) =>
  s === s.toLowerCase() || s === s.toUpperCase()
    ? s.toLowerCase().replace(/(^|[\s-])(\p{L})/gu, (_m, sep: string, c: string) => sep + c.toUpperCase())
    : s;
